import type { DetectorConfig, WorkspaceSummary } from "@spamjam/contracts";
import {
  digestFrequencies,
  digestFrequencyLabels,
  recentSpamPresentation,
  type DigestFrequency,
} from "@spamjam/contracts";
import {
  Activity,
  ArrowRight,
  Brain,
  Check,
  CircleAlert,
  CreditCard,
  ExternalLink,
  Flash,
  Inbox,
  KeyRound,
  LockKeyhole,
  MailCheck,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  Undo2,
  Users,
} from "./components/icons";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLoad } from "./hooks";
import { API_ORIGIN, api, endpoints } from "./lib/api";
import { authClient } from "./lib/auth";
import { formatDate } from "./lib/utils";
import { PRODUCT_NAME } from "./product.generated";
import { getHeroEmailInitials, heroEmailExamples } from "./hero-email-examples";
import { dashboardProtectionState } from "./dashboard-protection";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  ConfirmDialog,
  Field,
  Input,
  Pagination,
  PromptDialog,
  Select,
  Skeleton,
  Textarea,
} from "./components/ui";

export function LandingPage() {
  return (
    <div className="landing">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="marketing-nav">
        <Link to="/" aria-label="FuckSpam home">
          <span className="wordmark">{PRODUCT_NAME}</span>
        </Link>
        <nav>
          <a href="#how">How it works</a>
          <a href="#privacy">Privacy</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <div className="marketing-nav-actions">
          <Button render={<Link to="/login" />} variant="secondary">
            Log in
          </Button>
          <Button render={<Link to="/login" />}>
            <span className="desktop-cta-label">Start killing spam</span>
            <span className="mobile-cta-label">Start</span>
            <ArrowRight size={17} />
          </Button>
        </div>
      </header>
      <main id="main-content">
        <section className="hero">
          <div className="hero-copy">
            <Badge tone="jam">Jev-based spam filtering</Badge>
            <h1>Never see spam again.</h1>
            <p>
              Gmail&apos;s spam detection is broken. We catch the most
              sophisticated spam.
            </p>
            <div className="hero-actions">
              <Button render={<Link to="/login" />} size="lg">
                Start killing spam <ArrowRight size={18} />
              </Button>
              <Button render={<a href="#how" />} variant="secondary" size="lg">
                How it works
              </Button>
            </div>
            <div className="trust-row">
              <span>
                <Check /> Personal Gmail is free
              </span>
              <span>
                <Check /> Every action is reversible
              </span>
              <span>
                <Check /> Respects your privacy
              </span>
            </div>
          </div>
          <div
            className="hero-visual"
            aria-label="Example FuckSpam inbox cleanup"
          >
            <div className="orbit orbit-one" />
            <div className="orbit orbit-two" />
            <div className="inbox-card">
              <div className="inbox-top">
                <div>
                  <small>SPAM CAUGHT</small>
                  <strong>100%</strong>
                </div>
                <ShieldCheck />
              </div>
              <HeroInboxDemo />
            </div>
          </div>
        </section>
        <section id="how" className="section">
          <h2>Fight AI spam with better AI.</h2>
          <div className="feature-grid">
            <Feature
              icon={<Flash />}
              title="Instant detection"
              text="We use push notifications to kill spam before you even see it."
            />
            <Feature
              icon={<Brain />}
              title="Stay ahead of spammers"
              text="We use a cheap model like Jev, while spammers have to use expensive LLMs."
            />
            <Feature
              icon={<ShieldCheck />}
              title="Uses Gmail's native Spam"
              text="Messages are never deleted and you can undo any action."
            />
          </div>
        </section>
        <section id="privacy" className="privacy-band">
          <h2 className="privacy-title">
            <LockKeyhole aria-hidden="true" />
            <span>Your email stays in Gmail.</span>
          </h2>
          <p>
            Messages are only processed in-memory for spam detection and never
            stored on our servers or logs.{" "}
            <Link to="/privacy">Read our privacy policy.</Link>
          </p>
        </section>
        <section id="pricing" className="section pricing">
          <div>
            <h2>
              Free for you.
              <br />
              $1/user/month for teams.
            </h2>
          </div>
          <div className="price-cards">
            <Card>
              <Badge tone="good">Personal Gmail</Badge>
              <div className="price">$0</div>
              <Button render={<Link to="/login" />} variant="secondary">
                Start free
              </Button>
            </Card>
            <Card className="price-featured">
              <Badge tone="jam">Google Workspace</Badge>
              <div className="price">
                $1 <small>/ user / month</small>
              </div>
              <p>Billed yearly upfront at $12 per user.</p>
              <Button render={<Link to="/login" />}>Start 14-day trial</Button>
            </Card>
          </div>
        </section>
      </main>
      <footer>
        <span className="wordmark">{PRODUCT_NAME}</span>
        <div className="footer-meta">
          <span>&quot;I like spam.&quot; Said no one ever.</span>
          <div className="legal-links" aria-label="Legal information">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

type HeroInboxPhase = "judging" | "labeled" | "shifting";

function HeroInboxDemo() {
  const [startIndex, setStartIndex] = useState(0);
  const [phase, setPhase] = useState<HeroInboxPhase>("judging");
  const [clearedCount, setClearedCount] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setReducedMotion(mediaQuery.matches);

    syncPreference();
    mediaQuery.addEventListener("change", syncPreference);
    return () => mediaQuery.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setPhase("labeled");
      setClearedCount(heroEmailExamples[0]?.verdict === "caught" ? 1 : 0);
      return;
    }

    const delay = phase === "judging" ? 700 : phase === "labeled" ? 1_650 : 450;
    const timeout = window.setTimeout(() => {
      if (phase === "judging") {
        if (heroEmailExamples[startIndex]?.verdict === "caught") {
          setClearedCount((count) => count + 1);
        }
        setPhase("labeled");
        return;
      }

      if (phase === "labeled") {
        setPhase("shifting");
        return;
      }

      setStartIndex((index) => (index + 1) % heroEmailExamples.length);
      setPhase("judging");
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [phase, reducedMotion, startIndex]);

  const rowCount = phase === "shifting" ? 4 : 3;
  const visibleExamples = Array.from({ length: rowCount }, (_, offset) => {
    const index = (startIndex + offset) % heroEmailExamples.length;
    return { example: heroEmailExamples[index], index };
  });
  const clearedNoun = `real spam ${clearedCount === 1 ? "email" : "emails"} cleared`;

  return (
    <>
      <div className="mail-window">
        <div
          className={`mail-stack${phase === "shifting" ? " is-shifting" : ""}`}
        >
          {visibleExamples.map(({ example, index }, offset) => {
            if (!example) return null;

            const verdictVisible = offset === 0 && phase !== "judging";
            return (
              <div
                className={`mail-line ${example.verdict === "caught" ? "jammed" : "safe"}`}
                key={index}
              >
                <span className="mail-avatar">
                  {getHeroEmailInitials(example.sender)}
                </span>
                <div>
                  <b>{example.sender}</b>
                  <span>{example.subject}</span>
                </div>
                <span
                  className={`mail-verdict is-${example.verdict}${verdictVisible ? " is-visible" : ""}`}
                >
                  {verdictVisible && (
                    <Badge>
                      {example.verdict === "caught" ? "Caught" : "Passed"}
                    </Badge>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className="decision-pill"
        aria-label={`${clearedCount} ${clearedNoun} in this demo`}
      >
        <Sparkles size={16} />
        <span className="decision-count" key={clearedCount}>
          {clearedCount}
        </span>{" "}
        {clearedNoun}
      </div>
    </>
  );
}

export function PrivacyPage() {
  return (
    <PublicLegalPage title="Privacy Policy" updated="October 4, 2026">
      <p className="legal-lead">
        FuckSpam is a Gmail spam-detection service. This policy explains what
        data FuckSpam accesses, why it uses that data, when narrowly limited
        information is retained, and how you can export or delete your data.
      </p>

      <section>
        <h2>Data you provide</h2>
        <p>
          FuckSpam uses your email address for passwordless sign-in and stores
          the sessions, workspace membership, detector settings, and security
          records needed to operate your account. If you administer a paid
          Google Workspace domain, Stripe processes your checkout and billing
          information; FuckSpam stores Stripe resource identifiers and
          subscription status, not card numbers.
        </p>
      </section>

      <section>
        <h2>Google user data</h2>
        <p>
          Connecting Gmail is optional and separate from FuckSpam sign-in.
          FuckSpam requests <code>openid</code>, <code>email</code>, and{" "}
          <code>gmail.modify</code> to verify the mailbox identity and hosted
          domain, observe new Inbox messages, read the minimum message state
          needed for detection, apply Gmail’s native Spam labels, restore a
          message you mark Not spam, and keep Gmail watches synchronized.
        </p>
        <p>
          FuckSpam does not use Google user data for advertising, sell it,
          transfer it to data brokers, or use it to train generalized machine
          learning or artificial-intelligence models. FuckSpam’s use and
          transfer of information received from Google APIs adheres to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </section>

      <section>
        <h2>How Gmail content is handled</h2>
        <p>
          During ordinary filtering, sender and recipient text, subject, body,
          headers, and MIME content exist only in Worker memory. They are not
          written to FuckSpam’s databases, queues, dead letters, logs,
          analytics, or support systems.
        </p>
        <p>
          Content is retained only when you explicitly mark an auto-spam action
          as a false positive or consent once in Settings to contribute up to
          200 recent Gmail Spam messages for system evaluation. These samples
          are encrypted. A contributed evaluation sample cannot be withdrawn
          separately after submission.
        </p>
      </section>

      <section>
        <h2>Operational data and retention</h2>
        <p>FuckSpam retains only the control data needed to operate safely:</p>
        <ul>
          <li>
            encrypted Gmail refresh tokens, provider identifiers, watch cursors,
            and connection health;
          </li>
          <li>
            content-free scores, actions, configuration versions, timestamps,
            keyed hashes, and normalized error codes;
          </li>
          <li>
            encrypted, user-consented evaluation samples, aggregate evaluation
            reports for up to 90 days, and account exports for up to 24 hours;
            and
          </li>
          <li>
            authentication and billing records for their operational or legal
            retention period.
          </li>
        </ul>
      </section>

      <section>
        <h2>Service providers and disclosure</h2>
        <p>
          FuckSpam uses Cloudflare for hosting, storage, queues, and OTP email;
          Google for Gmail and Pub/Sub; TypeSafe to evaluate bounded message
          state transiently with a pinned decision model; and Stripe for domain
          workspace billing. Data is disclosed only as needed to provide those
          functions, comply with law, protect users and the service, or complete
          an action you request. FuckSpam does not sell personal information.
        </p>
      </section>

      <section>
        <h2>Your controls</h2>
        <p>
          You can pause or disconnect Gmail, revoke its Google grant, inspect
          active sessions, export your content-free account data, delete
          retained false-positive evidence, and delete your FuckSpam account
          from Settings. Evaluation contributions cannot be withdrawn
          independently. A successful account-deletion response is returned only
          after provider revocation, active private artifacts, and product
          control rows have been removed. A transient failure does not claim
          deletion succeeded and can be retried.
        </p>
      </section>

      <section>
        <h2>Security and changes</h2>
        <p>
          FuckSpam uses encrypted provider credentials, scoped authorization,
          exact-origin browser controls, signed webhooks, content-minimizing
          queues, and a global Gmail-mutation safety switch. No service can
          eliminate every risk. Material changes to how Google user data is used
          will be reflected here and presented for consent when required.
        </p>
      </section>

      <section>
        <h2>Questions and requests</h2>
        <p>
          Signed-in users can make privacy, export, revocation, and deletion
          requests from <Link to="/settings/security">Security settings</Link>.
          Before public launch, FuckSpam will also publish the verified support
          contact used by its Google OAuth consent screen.
        </p>
      </section>
    </PublicLegalPage>
  );
}

export function TermsPage() {
  return (
    <PublicLegalPage title="Terms of Service" updated="October 4, 2026">
      <p className="legal-lead">
        These terms govern use of FuckSpam. By using the service, you authorize
        FuckSpam to perform the mailbox and account actions you choose through
        the product.
      </p>
      <section>
        <h2>The service</h2>
        <p>
          FuckSpam observes new Gmail Inbox messages, evaluates likely unwanted
          outreach, and may apply Gmail’s reversible Spam state under FuckSpam’s
          deployment-managed system detector. Gmail remains the mailbox and
          message system of record. FuckSpam does not permanently delete mail.
        </p>
      </section>
      <section>
        <h2>Your account and authorization</h2>
        <p>
          You are responsible for your sign-in email, sessions, and use of
          mailboxes you are authorized to connect. Every workspace member must
          authorize their own Gmail account. You may revoke Gmail access or
          delete your FuckSpam account from Security settings.
        </p>
      </section>
      <section>
        <h2>Plans and billing</h2>
        <p>
          Personal consumer Gmail workspaces are free. A verified Google
          Workspace domain receives one 14-day organization trial and then costs
          $12 per active connected member each year through one Stripe
          subscription, billed yearly upfront (equivalent to $1 per user per
          month). Existing subscriptions keep their original Stripe price. The
          billing page shows current quantity and status. Cancellation and
          payment failure stop new paid protection when the subscription
          entitlement ends; they do not change existing Gmail messages or
          prevent export, disconnect, restoration, or deletion.
        </p>
      </section>
      <section>
        <h2>Acceptable use</h2>
        <p>
          Do not use FuckSpam to access another person’s mailbox without
          authorization, interfere with the service, evade limits, probe other
          users’ data, distribute malware, or violate applicable law or Google,
          Cloudflare, TypeSafe, or Stripe platform rules.
        </p>
      </section>
      <section>
        <h2>Safety and availability</h2>
        <p>
          Spam classification is probabilistic and may miss unwanted mail or
          move wanted mail. Actions are designed to be reversible, and the Not
          spam workflow is available for correction. FuckSpam may pause
          mutations, fail open, or limit processing to protect users during
          uncertainty, provider outages, billing lapses, or security events.
        </p>
      </section>
      <section>
        <h2>Changes and termination</h2>
        <p>
          FuckSpam may update these terms as the service changes and will post
          the revised date here. You may stop using the service at any time by
          disconnecting providers and deleting your account. FuckSpam may
          suspend abusive or unlawful use while preserving applicable export and
          deletion rights.
        </p>
      </section>
      <section>
        <h2>Privacy</h2>
        <p>
          The <Link to="/privacy">Privacy Policy</Link> explains how FuckSpam
          handles personal and Google user data and is part of these terms.
        </p>
      </section>
    </PublicLegalPage>
  );
}

function PublicLegalPage({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: React.ReactNode;
}) {
  return (
    <div className="legal-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="legal-nav">
        <Link to="/" aria-label="FuckSpam home">
          <span className="wordmark">{PRODUCT_NAME}</span>
        </Link>
        <Button render={<Link to="/login" />} variant="secondary">
          Sign in
        </Button>
      </header>
      <main id="main-content" className="legal-page">
        <div className="eyebrow">FuckSpam legal</div>
        <h1>{title}</h1>
        <p className="legal-updated">Effective and last updated: {updated}</p>
        <div className="legal-copy">{children}</div>
      </main>
      <footer className="legal-footer">
        <Link to="/">← FuckSpam home</Link>
        <div className="legal-links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <Card className="feature-card">
      <div className="feature-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{text}</p>
    </Card>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNextPath(params.get("next"));
  const health = useLoad(endpoints.health, []);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const canSubmit =
    !busy && !health.loading && health.data?.integrations.email !== false;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    const result = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: "sign-in",
    });
    setBusy(false);
    if (result.error)
      setError(result.error.message ?? "We couldn't send the code.");
    else {
      const verify = new URLSearchParams({ email });
      if (next) verify.set("next", next);
      navigate(`/verify?${verify}`);
    }
  };
  return (
    <AuthFrame>
      <h1>Welcome to FuckSpam</h1>
      {error && <Alert tone="error">{error}</Alert>}
      {health.data && !health.data.integrations.email && (
        <Alert>
          Email delivery is not configured yet. Production sign-in becomes
          available after the FuckSpam sender domain is onboarded.
        </Alert>
      )}
      {health.error && <Alert tone="error">{health.error.message}</Alert>}
      <form onSubmit={submit}>
        <Field label="Email address">
          <Input
            required
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.nativeEvent.isComposing &&
                canSubmit
              ) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder="you@example.com"
          />
        </Field>
        <Button disabled={!canSubmit} type="submit">
          {busy ? "Sending…" : "Send my code"}
          <ArrowRight size={17} />
        </Button>
      </form>
    </AuthFrame>
  );
}

export function VerifyPage() {
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  const next = safeNextPath(params.get("next"));
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [localCodeLoaded, setLocalCodeLoaded] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const attemptedLocalDelivery = useRef(false);
  const navigate = useNavigate();
  const loadLocalCode = async () => {
    const health = await endpoints.health();
    if (!health.integrations.localOtpStub || !email) return;
    const delivery = await api<{ code: string }>(
      `/v1/dev/otp?email=${encodeURIComponent(email)}`,
    );
    setCode(delivery.code);
    setLocalCodeLoaded(true);
  };
  useEffect(() => {
    if (!email || attemptedLocalDelivery.current) return;
    attemptedLocalDelivery.current = true;
    void loadLocalCode().catch(() => undefined);
  }, [email]);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(
      () => setResendCooldown((value) => Math.max(0, value - 1)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [resendCooldown > 0]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const result = await authClient.signIn.emailOtp({ email, otp: code });
    setBusy(false);
    if (result.error)
      setError(result.error.message ?? "That code did not work.");
    else navigate(next ?? "/dashboard");
  };
  return (
    <AuthFrame>
      <Badge tone="good">
        <MailCheck size={13} /> Code sent
      </Badge>
      <h1>Check your inbox</h1>
      <p>
        Enter the code sent to <strong>{email}</strong>. It expires in five
        minutes.
      </p>
      {localCodeLoaded && (
        <Alert>
          Local development delivery filled the memory-only code for you. This
          helper is unavailable in production.
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      <form onSubmit={submit}>
        <Field label="Six-digit code">
          <Input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="otp-input"
            placeholder="000000"
          />
        </Field>
        <Button disabled={busy || code.length !== 6} type="submit">
          {busy ? "Checking…" : "Continue"}
          <ArrowRight size={17} />
        </Button>
      </form>
      <Button
        variant="ghost"
        disabled={!email || resendCooldown > 0}
        onClick={async () => {
          setError("");
          setResendCooldown(30);
          const result = await authClient.emailOtp.sendVerificationOtp({
            email,
            type: "sign-in",
          });
          if (result.error) {
            setResendCooldown(0);
            setError(result.error.message ?? "We couldn't send a new code.");
          } else await loadLocalCode().catch(() => undefined);
        }}
      >
        {resendCooldown > 0
          ? `Send a new code in ${resendCooldown}s`
          : "Send a new code"}
      </Button>
    </AuthFrame>
  );
}

function safeNextPath(value: string | null): string | null {
  return value && value.startsWith("/") && !value.startsWith("//")
    ? value
    : null;
}

function AuthFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth-page">
      <Link className="wordmark" to="/">
        {PRODUCT_NAME}
      </Link>
      <Card className="auth-card">{children}</Card>
      <Link to="/">← Back home</Link>
      <div className="legal-links" aria-label="Legal information">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
      </div>
    </main>
  );
}

export function DashboardPage() {
  const data = useLoad(
    async () =>
      Promise.all([
        endpoints.health(),
        endpoints.accounts(),
        endpoints.workspaces(),
        endpoints.coverage(),
      ]),
    [],
  );
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const connect = async () => {
    setConnecting(true);
    setConnectionError("");
    try {
      const { url } = await api<{ url: string }>(
        "/v1/integrations/google/start",
        {
          method: "POST",
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/dashboard`,
          }),
        },
      );
      window.location.assign(url);
    } catch (cause) {
      setConnectionError(
        cause instanceof Error ? cause.message : "Connection failed",
      );
      setConnecting(false);
    }
  };
  if (data.loading) return <PageLoading />;
  if (data.error) return <PageError error={data.error} />;
  const [healthData, accountData, workspaceData, coverageData] = data.data!;
  const killSwitch = healthData.protection.mutationKillSwitchEnabled;
  const needsAction = accountData.accounts.length === 0;
  const protection = dashboardProtectionState({
    accounts: accountData.accounts,
    workspaces: workspaceData.workspaces,
    killSwitch,
  });
  const protectionActive = protection.active;
  return (
    <Page
      title="Dashboard"
      action={
        <Button variant="secondary" onClick={() => void data.reload()}>
          <RefreshCw size={16} /> Refresh
        </Button>
      }
    >
      {connectionError && <Alert tone="error">{connectionError}</Alert>}
      <div className="metric-grid">
        <Metric
          label="Connected mailboxes"
          value={String(accountData.accounts.length)}
          detail={protection.connectedDetail}
          tone={protection.connectedTone}
          action={
            needsAction ? (
              <Button
                onClick={() => void connect()}
                disabled={connecting || !healthData.integrations.google}
              >
                <Plus size={16} />
                {connecting ? "Opening Google…" : "Connect Gmail"}
              </Button>
            ) : undefined
          }
        />
        <Metric
          label="Filter status"
          value={protection.filterStatus}
          detail={protection.filterDetail}
          tone={protection.filterTone}
          action={
            protection.billingRequired ? (
              <Button render={<Link to="/billing" />}>
                <CreditCard size={16} /> Activate subscription
              </Button>
            ) : undefined
          }
        />
      </div>
      <Card>
        <CardHeader>
          <div>
            <h3>Pipeline coverage · last 24 hours</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className="detail-grid">
            <Detail
              label="Classified negative"
              value={String(coverageData.coverage.classifiedNegative)}
            />
            <Detail
              label="Classified positive"
              value={String(coverageData.coverage.classifiedPositive)}
            />
            <Detail
              label="Skipped ineligible"
              value={String(coverageData.coverage.skippedIneligible)}
            />
            <Detail
              label="Skipped missing"
              value={String(coverageData.coverage.skippedMissing)}
            />
            <Detail
              label="Never reached / stalled"
              value={String(coverageData.coverage.neverReachedOrStalled)}
            />
          </div>
          {coverageData.coverage.neverReachedOrStalled > 0 && (
            <Alert tone="error">
              Some messages did not complete the decision path. Automatic action
              remains fail-open while the queue retries or dead-letters them.
            </Alert>
          )}
        </CardContent>
      </Card>
      <div className="two-column">
        <Card>
          <CardHeader>
            <div>
              <h3>Mailbox health</h3>
            </div>
            <Link to="/accounts">Manage</Link>
          </CardHeader>
          <CardContent>
            {accountData.accounts.length ? (
              accountData.accounts.map((account) => (
                <div className="list-row" key={account.id}>
                  <span className="round-icon">
                    <Inbox />
                  </span>
                  <div>
                    <strong>{account.displayAddress}</strong>
                    <span>Real-time Gmail watch active</span>
                  </div>
                  <Status
                    status={
                      account.healthStatus === "degraded"
                        ? "degraded"
                        : account.status
                    }
                  />
                </div>
              ))
            ) : (
              <Empty icon={<Inbox />} title="No mailboxes yet" />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div>
              <h3>Protection</h3>
            </div>
          </CardHeader>
          <CardContent>
            <ol className="steps">
              <li className={accountData.accounts.length ? "done" : "current"}>
                <span>{accountData.accounts.length ? <Check /> : 1}</span>
                <div>
                  <strong>Connect Gmail</strong>
                </div>
              </li>
              <li className={protectionActive ? "done" : ""}>
                <span>{protectionActive ? <Check /> : 2}</span>
                <div>
                  <strong>Detect new unwanted mail</strong>
                </div>
              </li>
              <li className={protectionActive ? "done" : ""}>
                <span>{protectionActive ? <Check /> : 3}</span>
                <div>
                  <strong>Move matches to Gmail Spam</strong>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}

export function AccountsPage() {
  const accounts = useLoad(endpoints.accounts, []);
  const health = useLoad(endpoints.health, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [disconnectTarget, setDisconnectTarget] = useState<string | null>(null);
  const connect = async () => {
    setBusy(true);
    setError("");
    try {
      const { url } = await api<{ url: string }>(
        "/v1/integrations/google/start",
        {
          method: "POST",
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/accounts`,
          }),
        },
      );
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection failed");
      setBusy(false);
    }
  };
  const mutate = async (id: string, action: "pause" | "resume") => {
    setError("");
    try {
      await api(`/v1/email-accounts/${id}/${action}`, { method: "POST" });
      await accounts.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Update failed");
    }
  };
  const disconnect = async (id: string) => {
    setError("");
    try {
      await api(`/v1/email-accounts/${id}`, { method: "DELETE" });
      await accounts.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Disconnect failed");
    }
  };
  return (
    <Page
      title="Mailboxes"
      action={
        <Button
          onClick={connect}
          disabled={
            busy || health.loading || health.data?.integrations.google === false
          }
        >
          <Plus size={17} /> {busy ? "Opening Google…" : "Connect Gmail"}
        </Button>
      }
    >
      {(error || accounts.error) && (
        <Alert tone="error">{error || accounts.error?.message}</Alert>
      )}
      {health.data && !health.data.integrations.google && (
        <Alert>
          <strong>Google connection is dormant.</strong> Add the OAuth and
          Pub/Sub configuration when the Google Cloud project is ready.
        </Alert>
      )}
      <Card>
        <CardHeader>
          <div>
            <h3>Your connections</h3>
          </div>
        </CardHeader>
        <CardContent>
          {accounts.loading ? (
            <Skeleton className="h-24" />
          ) : accounts.data?.accounts.length ? (
            accounts.data.accounts.map((account) => (
              <div className="account-row" key={account.id}>
                <span className="gmail-mark">M</span>
                <div>
                  <strong>{account.displayAddress}</strong>
                  <span>
                    Watch active until {formatDate(account.watchExpiresAt)}
                  </span>
                </div>
                <div>
                  <Status
                    status={
                      account.healthStatus === "degraded"
                        ? "degraded"
                        : account.status
                    }
                  />
                  {account.lastErrorCode && (
                    <small>{account.lastErrorCode}</small>
                  )}
                </div>
                {["reconnect_required", "scope_required"].includes(
                  account.status,
                ) ? (
                  <Button variant="secondary" onClick={() => void connect()}>
                    Reconnect
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void mutate(
                        account.id,
                        account.status === "paused" ? "resume" : "pause",
                      )
                    }
                  >
                    {account.status === "paused" ? "Resume" : "Pause"}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setDisconnectTarget(account.id)}
                  aria-label={`Disconnect ${account.displayAddress}`}
                >
                  <Trash2 size={16} /> Disconnect
                </Button>
              </div>
            ))
          ) : (
            <Empty
              icon={<Inbox />}
              title="Bring your first inbox"
              action={
                <Button
                  onClick={connect}
                  disabled={health.data?.integrations.google === false}
                >
                  Connect Gmail
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>
      <ConfirmDialog
        open={Boolean(disconnectTarget)}
        title="Disconnect Gmail?"
        description="FuckSpam will revoke provider access and delete this mailbox connection. Gmail messages are not deleted."
        confirmLabel="Disconnect"
        destructive
        onOpenChange={(open) => {
          if (!open) setDisconnectTarget(null);
        }}
        onConfirm={async () => {
          if (disconnectTarget) await disconnect(disconnectTarget);
        }}
      />
    </Page>
  );
}

export function WorkspacePage({
  showMembers = false,
}: {
  showMembers?: boolean;
}) {
  const workspaces = useLoad(endpoints.workspaces, []);
  return (
    <Page
      title={showMembers ? "Members" : "Workspace"}
      subtitle="Organization boundaries never grant access to another person’s mailbox."
    >
      {workspaces.error && (
        <Alert tone="error">{workspaces.error.message}</Alert>
      )}
      {workspaces.loading ? (
        <PageLoading />
      ) : (
        workspaces.data?.workspaces.map((workspace) => (
          <WorkspaceCard
            key={workspace.id}
            workspace={workspace}
            showMembers={showMembers}
            onChanged={workspaces.reload}
          />
        ))
      )}
    </Page>
  );
}

function WorkspaceCard({
  workspace,
  showMembers,
  onChanged,
}: {
  workspace: WorkspaceSummary;
  showMembers: boolean;
  onChanged: () => Promise<void>;
}) {
  const members = useLoad(
    async () =>
      showMembers && workspace.kind === "domain"
        ? api<{ members: Array<Record<string, unknown>> }>(
            `/v1/workspaces/${encodeURIComponent(workspace.id)}/members`,
          )
        : { members: [] },
    [showMembers, workspace.id, workspace.kind],
  );
  const [error, setError] = useState("");
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const removeMember = async (memberId: string) => {
    setError("");
    try {
      await api(
        `/v1/workspaces/${encodeURIComponent(workspace.id)}/members/${encodeURIComponent(memberId)}`,
        { method: "DELETE" },
      );
      await Promise.all([members.reload(), onChanged()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Removal failed");
    }
  };
  return (
    <Card>
      <CardHeader>
        <div>
          <Badge tone={workspace.kind === "personal" ? "good" : "jam"}>
            {workspace.kind === "personal"
              ? `Personal Gmail · ${short(workspace.id)}`
              : workspace.hostedDomain}
          </Badge>
          <h3>
            {workspace.kind === "personal"
              ? "Your personal workspace"
              : workspace.hostedDomain}
          </h3>
          <p>
            {workspace.kind === "personal"
              ? "One user, free forever."
              : "Verified by Google’s hosted-domain claim."}
          </p>
        </div>
        <Status status={workspace.entitlement} />
      </CardHeader>
      <CardContent>
        {(error || members.error) && (
          <Alert tone="error">{error || members.error?.message}</Alert>
        )}
        <div className="detail-grid">
          <Detail label="Your role" value={workspace.role.replace("_", " ")} />
          <Detail label="Entitlement" value={workspace.entitlement} />
          <Detail
            label="Active members"
            value={String(workspace.activeMemberQuantity)}
          />
          <Detail
            label="Trial ends"
            value={formatDate(workspace.trialEndsAt)}
          />
        </div>
        {showMembers && workspace.kind === "domain" && (
          <div className="member-block">
            <h4>{workspace.hostedDomain} members</h4>
            {members.loading ? (
              <Skeleton className="h-24" />
            ) : (
              members.data?.members.map((member, index) => (
                <div className="list-row" key={String(member.id ?? index)}>
                  <span className="round-icon">
                    <Users />
                  </span>
                  <div>
                    <strong>{String(member.name)}</strong>
                    <span>
                      {String(member.email)} · {String(member.role)}
                    </span>
                  </div>
                  <Status
                    status={member.connected ? "connected" : "not connected"}
                  />
                  {["owner", "billing_admin"].includes(workspace.role) &&
                    member.role !== "owner" &&
                    !member.is_self && (
                      <Button
                        variant="secondary"
                        onClick={() => setRemoveTarget(String(member.id))}
                      >
                        Remove
                      </Button>
                    )}
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={Boolean(removeTarget)}
        title={`Remove member from ${workspace.hostedDomain ?? "this workspace"}?`}
        description="FuckSpam will revoke this member’s Gmail connection and remove their workspace membership."
        confirmLabel="Remove member"
        destructive
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        onConfirm={async () => {
          if (removeTarget) await removeMember(removeTarget);
        }}
      />
    </Card>
  );
}

export function DetectorPage() {
  const configs = useLoad(endpoints.configs, []);
  const health = useLoad(endpoints.health, []);
  const current = configs.data?.configs[0];
  const [prompt, setPrompt] = useState("");
  const [review, setReview] = useState("0.20");
  const [auto, setAuto] = useState("0.40");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [testFrom, setTestFrom] = useState("");
  const [testSubject, setTestSubject] = useState("");
  const [testBody, setTestBody] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    score: number | null;
    outcome: string;
    model: string;
    latencyMs: number;
  } | null>(null);
  const editedPrompt = prompt || current?.prompt || "";
  const save = async () => {
    setError("");
    try {
      const result = await api<{ config: DetectorConfig }>(
        "/v1/detector-configs",
        {
          method: "POST",
          body: JSON.stringify({
            prompt: editedPrompt,
            reviewThreshold: Number(review),
            autoSpamThreshold: Number(auto),
            bodyCharacterLimit: 6000,
          }),
        },
      );
      setMessage(
        `Draft ${short(result.config.id)} created. Run an evaluation before activation.`,
      );
      await configs.reload();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Draft creation failed",
      );
    }
  };
  const evaluate = async (id: string) => {
    setError("");
    try {
      const result = await api<{ runId: string }>(
        `/v1/detector-configs/${id}/evaluate`,
        { method: "POST" },
      );
      setMessage(`Evaluation ${short(result.runId)} queued.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Evaluation failed");
    }
  };
  const canary = async (id: string) => {
    setError("");
    try {
      await api(`/v1/detector-configs/${id}/canary`, {
        method: "POST",
        body: JSON.stringify({ percentage: 0.05 }),
      });
      setMessage(
        "A 5% deterministic canary is active. The remaining traffic stays on the prior active configuration or shadow mode.",
      );
      await configs.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Canary failed");
    }
  };
  const activate = async (id: string) => {
    setError("");
    try {
      await api(`/v1/detector-configs/${id}/activate`, { method: "POST" });
      setMessage(
        "The passing canary configuration is now active for all connected mailboxes.",
      );
      await configs.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Activation failed");
    }
  };
  const rollback = async (id: string) => {
    setError("");
    try {
      await api(`/v1/detector-configs/${id}/rollback`, { method: "POST" });
      setMessage("The previous passing detector version is active again.");
      await configs.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Rollback failed");
    }
  };
  const test = async () => {
    if (!current) return;
    setError("");
    setTestBusy(true);
    try {
      setTestResult(
        await api(`/v1/detector-configs/${current.id}/test`, {
          method: "POST",
          body: JSON.stringify({
            from: testFrom,
            subject: testSubject,
            body: testBody,
          }),
        }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Test failed");
    } finally {
      setTestBusy(false);
    }
  };
  return (
    <Page title="Detector">
      {message && <Alert tone="success">{message}</Alert>}
      {(error || configs.error) && (
        <Alert tone="error">{error || configs.error?.message}</Alert>
      )}
      {health.data && !health.data.integrations.typesafe && (
        <Alert>
          TypeSafe scoring is dormant until its API key is configured. You can
          prepare immutable drafts now; tests and evaluations stay disabled.
        </Alert>
      )}
      <div className="two-column detector-layout">
        <Card>
          <CardHeader>
            <div>
              <h3>Current versions</h3>
            </div>
          </CardHeader>
          <CardContent>
            {configs.loading ? (
              <Skeleton className="h-40" />
            ) : (
              configs.data?.configs.map((config) => (
                <div className="config-row" key={config.id}>
                  <div>
                    <strong>{short(config.id)}</strong>
                    <span>
                      {formatDate(config.createdAt)} · {config.model}
                    </span>
                  </div>
                  <Status status={config.status} />
                  <div className="row-actions">
                    <Button
                      variant="secondary"
                      disabled={health.data?.integrations.typesafe === false}
                      onClick={() => void evaluate(config.id)}
                    >
                      Evaluate
                    </Button>
                    {config.status === "shadow" && (
                      <Button
                        variant="secondary"
                        onClick={() => void canary(config.id)}
                      >
                        Start 5% canary
                      </Button>
                    )}
                    {config.status === "canary" && (
                      <Button onClick={() => void activate(config.id)}>
                        Activate
                      </Button>
                    )}
                    {config.status === "retired" && (
                      <Button
                        variant="secondary"
                        onClick={() => setRollbackTarget(config.id)}
                      >
                        Roll back
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div>
              <h3>Create a draft</h3>
              <p>The active version will remain untouched.</p>
            </div>
          </CardHeader>
          <CardContent>
            <Field
              label="Spam decision prompt"
              hint="Email content is always treated as untrusted data."
            >
              <Textarea
                rows={15}
                value={editedPrompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </Field>
            <div className="form-grid">
              <Field label="Review threshold">
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                />
              </Field>
              <Field label="Auto-spam threshold">
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={auto}
                  onChange={(e) => setAuto(e.target.value)}
                />
              </Field>
            </div>
            <Button
              disabled={
                editedPrompt.length < 200 || Number(review) > Number(auto)
              }
              onClick={() => void save()}
            >
              Save immutable draft
            </Button>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <div>
            <h3>Single-message test</h3>
            <p>
              Test the newest detector version without reading or changing
              Gmail.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="form-grid">
            <Field label="From">
              <Input
                value={testFrom}
                onChange={(event) => setTestFrom(event.target.value)}
                placeholder="Pat Example <pat@example.com>"
              />
            </Field>
            <Field label="Subject">
              <Input
                value={testSubject}
                onChange={(event) => setTestSubject(event.target.value)}
                placeholder="Last note"
              />
            </Field>
          </div>
          <Field label="Current message body">
            <Textarea
              rows={7}
              value={testBody}
              onChange={(event) => setTestBody(event.target.value)}
            />
          </Field>
          <Button
            disabled={
              !current ||
              testBusy ||
              !testBody.trim() ||
              health.data?.integrations.typesafe === false
            }
            onClick={() => void test()}
          >
            {testBusy ? "Testing…" : "Run safe test"}
          </Button>
          {testResult && (
            <Alert tone="success">
              {testResult.outcome.replaceAll("_", " ")}
              {testResult.score === null
                ? " · deterministic allow, model not called"
                : ` · ${(testResult.score * 100).toFixed(1)}% · ${testResult.model} · ${testResult.latencyMs} ms`}
            </Alert>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog
        open={Boolean(rollbackTarget)}
        title="Roll back detector protection?"
        description="The selected previously passing configuration will replace the current active or canary version. Historical decisions remain unchanged."
        confirmLabel="Roll back"
        onOpenChange={(open) => {
          if (!open) setRollbackTarget(null);
        }}
        onConfirm={async () => {
          if (rollbackTarget) await rollback(rollbackTarget);
        }}
      />
    </Page>
  );
}

export function RecentSpamPage() {
  const [params, setParams] = useSearchParams();
  const decision = params.get("decision");
  const accounts = useLoad(endpoints.accounts, []);
  const [accountFilter, setAccountFilter] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<string | null>>([]);
  const recent = useLoad(
    () => endpoints.recentSpam(cursor, accountFilter || null, decision),
    [cursor, accountFilter, decision],
  );
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [evidenceTarget, setEvidenceTarget] = useState<string | null>(null);
  const disconnectedAccounts =
    accounts.data?.accounts.filter(
      (account) =>
        (!accountFilter || account.id === accountFilter) &&
        ["reconnect_required", "scope_required"].includes(account.status),
    ) ?? [];
  const restore = async (id: string) => {
    setError("");
    try {
      const result = await api<{ correctionId: string }>(
        `/v1/decisions/${id}/not-spam`,
        { method: "POST" },
      );
      setNote(
        `Restored in Gmail. Correction ${short(result.correctionId)} was recorded for future system evaluation.`,
      );
      await recent.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Restore failed");
    }
  };
  const deleteEvidence = async (correctionId: string) => {
    setError("");
    try {
      await api(`/v1/corrections/${correctionId}/evidence`, {
        method: "DELETE",
      });
      setNote(
        "Encrypted correction evidence deleted. The content-free audit remains.",
      );
      await recent.reload();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Evidence deletion failed",
      );
    }
  };
  return (
    <Page
      title="Recent auto-spam"
      subtitle="Message details are fetched live from Gmail and are not stored by FuckSpam."
    >
      {note && <Alert tone="success">{note}</Alert>}
      {(error || recent.error || accounts.error) && (
        <Alert tone="error">
          {error || recent.error?.message || accounts.error?.message}
        </Alert>
      )}
      {disconnectedAccounts.length > 0 && (
        <Alert tone="error">
          <strong>Gmail access expired.</strong> Message details are hidden, but
          the auto-spam audit records are intact. Reconnect Gmail to load the
          sender, subject, and restore controls.{" "}
          <Button variant="ghost" render={<Link to="/accounts" />}>
            Reconnect Gmail
          </Button>
        </Alert>
      )}
      {decision && (
        <Alert>
          Review this message from your email summary, then choose Not spam to
          restore it to your inbox.{" "}
          <Button
            variant="ghost"
            onClick={() => {
              setParams({});
              setCursor(null);
              setHistory([]);
            }}
          >
            View all recent spam
          </Button>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <div>
            <h3>Automatic actions</h3>
          </div>
          <label className="field compact-field">
            <span className="field-label">Mailbox</span>
            <Select
              value={accountFilter}
              onChange={(event) => {
                setAccountFilter(event.target.value);
                setCursor(null);
                setHistory([]);
              }}
            >
              <option value="">All mailboxes</option>
              {accounts.data?.accounts.map((account) => (
                <option value={account.id} key={account.id}>
                  {account.displayAddress}
                </option>
              ))}
            </Select>
          </label>
        </CardHeader>
        <CardContent>
          {recent.loading ? (
            <Skeleton className="h-48" />
          ) : recent.data?.items.length ? (
            recent.data.items.map((item) => {
              const view = recentSpamPresentation(item);
              return (
                <div className="spam-row" key={item.decisionId}>
                  <div className="score-ring">
                    <div className="score-value">
                      {view.score}
                      <small>%</small>
                    </div>
                  </div>
                  <div>
                    <strong>{view.subject}</strong>
                    <span>{view.sender}</span>
                    <small>
                      {formatDate(item.actionTime)} · {item.model} · config{" "}
                      {short(item.configId)}
                    </small>
                  </div>
                  <div className="row-actions spam-row-actions">
                    <div className="spam-action-open">
                      {view.openUrl && (
                        <Button
                          render={
                            <a
                              href={view.openUrl}
                              target="_blank"
                              rel="noreferrer"
                            />
                          }
                          variant="ghost"
                        >
                          Open <ExternalLink size={15} />
                        </Button>
                      )}
                    </div>
                    <div className="spam-action-status">
                      {!view.restored && item.message && (
                        <Button
                          variant="secondary"
                          onClick={() => void restore(item.decisionId)}
                        >
                          <Undo2 size={16} /> Not spam
                        </Button>
                      )}
                      {view.restored && <Badge tone="good">Restored</Badge>}
                    </div>
                    <div className="spam-action-evidence">
                      {item.correctionId && item.evidenceRetained && (
                        <Button
                          variant="ghost"
                          onClick={() => setEvidenceTarget(item.correctionId)}
                        >
                          Delete evidence
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <Empty
              icon={<ShieldCheck />}
              title={
                decision
                  ? "This message is no longer available"
                  : "Nothing moved yet"
              }
            />
          )}
          <Pagination>
            <Button
              variant="secondary"
              disabled={history.length === 0}
              onClick={() => {
                const previous = history.at(-1) ?? null;
                setHistory((items) => items.slice(0, -1));
                setCursor(previous);
              }}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={!recent.data?.nextCursor}
              onClick={() => {
                if (!recent.data?.nextCursor) return;
                setHistory((items) => [...items, cursor]);
                setCursor(recent.data.nextCursor);
              }}
            >
              Next
            </Button>
          </Pagination>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={Boolean(evidenceTarget)}
        title="Delete correction evidence?"
        description="The encrypted subject and message content will be erased. The content-free correction audit remains."
        confirmLabel="Delete evidence"
        destructive
        onOpenChange={(open) => {
          if (!open) setEvidenceTarget(null);
        }}
        onConfirm={async () => {
          if (evidenceTarget) await deleteEvidence(evidenceTarget);
        }}
      />
    </Page>
  );
}

function SpamDigestSettings() {
  const preferences = useLoad(endpoints.digestPreferences, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const change = async (frequency: DigestFrequency) => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api("/v1/spam-digest", {
        method: "PUT",
        body: JSON.stringify({ frequency }),
      });
      await preferences.reload();
      setSaved(true);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not save delivery preference",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <div>
          <h3>Your summary, your schedule</h3>
          <p>
            Choose whether to receive recent auto-spam by email. Delivery
            follows your signup date when enabled.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {(error || preferences.error) && (
          <Alert tone="error">{error || preferences.error?.message}</Alert>
        )}
        {saved && (
          <Alert tone="success">
            Delivery preference saved. Your spam protection stays active.
          </Alert>
        )}
        {preferences.loading ? (
          <Skeleton className="h-24" />
        ) : (
          preferences.data && (
            <>
              <p>
                Currently:{" "}
                <strong>
                  {
                    digestFrequencyLabels[
                      preferences.data.preferences.frequency
                    ]
                  }
                </strong>
                {preferences.data.preferences.nextDeliveryAt && (
                  <>
                    {" "}
                    · Next summary:{" "}
                    {formatDate(
                      preferences.data.preferences.nextDeliveryAt,
                    )}{" "}
                    (within an hour)
                  </>
                )}
              </p>
              <div
                className="row-actions digest-frequency-actions"
                role="group"
                aria-label="Email summary delivery"
              >
                {digestFrequencies.map((frequency) => (
                  <Button
                    key={frequency}
                    variant={
                      frequency === preferences.data!.preferences.frequency
                        ? "primary"
                        : "secondary"
                    }
                    disabled={busy}
                    aria-pressed={
                      frequency === preferences.data!.preferences.frequency
                    }
                    onClick={() => void change(frequency)}
                  >
                    {digestFrequencyLabels[frequency]}
                  </Button>
                ))}
              </div>
            </>
          )
        )}
      </CardContent>
    </Card>
  );
}

export function EvaluationsPage() {
  const evaluations = useLoad(endpoints.evaluations, []);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState("");
  const openReport = async (runId: string) => {
    setError("");
    try {
      const response = await fetch(
        `${API_ORIGIN}/v1/evaluations/${encodeURIComponent(runId)}/report?format=html`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("The evaluation report is unavailable");
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Report failed");
    }
  };
  return (
    <Page
      title="Evaluations"
      subtitle="Content-free quality reports gate every detector activation."
    >
      {(error || evaluations.error) && (
        <Alert tone="error">{error || evaluations.error?.message}</Alert>
      )}
      <Card>
        <CardHeader>
          <div>
            <h3>Runs</h3>
            <p>Evaluation uses the last 200 spam</p>
          </div>
        </CardHeader>
        <CardContent>
          {evaluations.loading ? (
            <Skeleton className="h-40" />
          ) : evaluations.data?.runs.length ? (
            evaluations.data.runs.map((run) => {
              const summary = evaluationSummary(run.aggregate_metrics_json);
              return (
                <div className="evaluation-row" key={String(run.id)}>
                  <span className="round-icon">
                    <Activity />
                  </span>
                  <div>
                    <strong>Run {short(String(run.id))}</strong>
                    <span>
                      {Number(run.sample_count ?? 0)} cases ·{" "}
                      {formatDate(String(run.created_at))}
                    </span>
                    {summary?.reviewComplete && (
                      <small>
                        Precision {(summary.precision * 100).toFixed(1)}% ·
                        Recall {(summary.recall * 100).toFixed(1)}% ·{" "}
                        {summary.falsePositivesPerThousandWanted.toFixed(1)} FP
                        /1,000 wanted
                      </small>
                    )}
                  </div>
                  <Status status={String(run.status)} />
                  {Boolean(run.completed_at) && summary?.reviewComplete && (
                    <Button
                      variant="ghost"
                      onClick={() => void openReport(String(run.id))}
                    >
                      Report <ExternalLink size={15} />
                    </Button>
                  )}
                  {Boolean(run.completed_at) && !summary?.reviewComplete && (
                    <small>
                      Finish blind labels, then rerun from Detector to unlock
                      the report.
                    </small>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setSelected(
                        selected === String(run.id) ? null : String(run.id),
                      )
                    }
                  >
                    {selected === String(run.id) ? "Close" : "Review cases"}
                  </Button>
                </div>
              );
            })
          ) : (
            <Empty
              icon={<Activity />}
              title="No evaluation runs"
              text="Start one from Detector after connecting Gmail."
            />
          )}
        </CardContent>
      </Card>
      {selected && <EvaluationCases runId={selected} />}
    </Page>
  );
}

function EvaluationCases({ runId }: { runId: string }) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<string | null>>([]);
  const cases = useLoad(
    () =>
      api<{
        cases: Array<{
          id: string;
          split: string;
          label: string | null;
          source: string;
          score: number | null;
          rationaleCategory: string | null;
          adjudicationStatus: string;
          labelRevision: number;
          message: { sender: string; subject: string } | null;
        }>;
        nextCursor: string | null;
      }>(
        `/v1/evaluations/${runId}/cases${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      ),
    [runId, cursor],
  );
  const [error, setError] = useState("");
  const [labelTarget, setLabelTarget] = useState<{
    id: string;
    value: string;
    rationale: string;
  } | null>(null);
  const requestLabel = (id: string, value: string) => {
    const defaults: Record<string, string> = {
      spam: "unsolicited_outreach",
      wanted: "expected_or_transactional",
      provider_error: "provider_misclassification",
      ambiguous: "insufficient_context",
    };
    setLabelTarget({
      id,
      value,
      rationale: defaults[value] ?? "reviewer_judgment",
    });
  };
  const label = async (
    id: string,
    value: string,
    rationaleCategory: string,
  ) => {
    setError("");
    try {
      await api(`/v1/evaluation-cases/${id}/label`, {
        method: "POST",
        body: JSON.stringify({ label: value, rationaleCategory }),
      });
      await cases.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Labeling failed");
    }
  };
  return (
    <Card>
      <CardHeader>
        <div>
          <h3>Blind adjudication</h3>
          <p>
            Choose the wanted-mail truth label. Scores stay hidden until every
            case in the run is adjudicated.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {(error || cases.error) && (
          <Alert tone="error">{error || cases.error?.message}</Alert>
        )}
        {cases.loading ? (
          <Skeleton className="h-40" />
        ) : (
          cases.data?.cases.map((item) => (
            <div className="spam-row" key={item.id}>
              <span className="round-icon">
                <MailCheck />
              </span>
              <div>
                <strong>
                  {item.message?.subject ?? "Message unavailable"}
                </strong>
                <span>{item.message?.sender ?? "Metadata-only case"}</span>
                <small>
                  {item.source.replaceAll("_", " ")} · {item.split}
                  {item.label && item.score !== null
                    ? ` · score ${item.score.toFixed(3)}`
                    : ""}
                  {item.adjudicationStatus === "resolved_disagreement"
                    ? " · revised after disagreement"
                    : ""}
                </small>
              </div>
              <div className="row-actions">
                {item.label && (
                  <Badge tone="good">{item.label.replaceAll("_", " ")}</Badge>
                )}
                <Button
                  variant="secondary"
                  disabled={item.label === "spam"}
                  onClick={() => requestLabel(item.id, "spam")}
                >
                  Spam
                </Button>
                <Button
                  variant="secondary"
                  disabled={item.label === "wanted"}
                  onClick={() => requestLabel(item.id, "wanted")}
                >
                  Wanted
                </Button>
                {item.source === "provider_spam_seed" && (
                  <Button
                    variant="secondary"
                    disabled={item.label === "provider_error"}
                    onClick={() => requestLabel(item.id, "provider_error")}
                  >
                    Gmail error
                  </Button>
                )}
                <Button
                  variant="ghost"
                  disabled={item.label === "ambiguous"}
                  onClick={() => requestLabel(item.id, "ambiguous")}
                >
                  Unsure
                </Button>
              </div>
            </div>
          ))
        )}
        <Pagination>
          <Button
            variant="secondary"
            disabled={history.length === 0}
            onClick={() => {
              const previous = history.at(-1) ?? null;
              setHistory((items) => items.slice(0, -1));
              setCursor(previous);
            }}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={!cases.data?.nextCursor}
            onClick={() => {
              if (!cases.data?.nextCursor) return;
              setHistory((items) => [...items, cursor]);
              setCursor(cases.data.nextCursor);
            }}
          >
            Next
          </Button>
        </Pagination>
      </CardContent>
      <PromptDialog
        open={Boolean(labelTarget)}
        title="Record adjudication rationale"
        description="Use a short content-free category. Do not copy message text into this field."
        label="Rationale category"
        defaultValue={labelTarget?.rationale ?? "reviewer_judgment"}
        confirmLabel="Save label"
        onOpenChange={(open) => {
          if (!open) setLabelTarget(null);
        }}
        onConfirm={async (rationale) => {
          if (labelTarget)
            await label(labelTarget.id, labelTarget.value, rationale);
        }}
      />
    </Card>
  );
}

export function BillingPage() {
  const [params] = useSearchParams();
  const workspaces = useLoad(endpoints.workspaces, []);
  const health = useLoad(endpoints.health, []);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState("");
  const [billingBusy, setBillingBusy] = useState("");
  const checkoutStatus = params.get("checkout");
  const checkoutWorkspace = params.get("workspace");
  const domains =
    workspaces.data?.workspaces.filter((w) => w.kind === "domain") ?? [];
  const personals =
    workspaces.data?.workspaces.filter((w) => w.kind === "personal") ?? [];
  const recoveryOffers = domains.filter((workspace) =>
    ["pending", "failed"].includes(
      workspace.billingRecovery?.status ?? "unavailable",
    ),
  );
  const paymentInProgressWorkspaceIds = domains
    .filter(
      (workspace) =>
        checkoutStatus === "success" &&
        workspace.entitlement !== "paid" &&
        (!checkoutWorkspace || checkoutWorkspace === workspace.id),
    )
    .map((workspace) => workspace.id);
  const paymentInProgressKey = paymentInProgressWorkspaceIds.join(",");
  useEffect(() => {
    if (!paymentInProgressKey) return;
    let reloadInFlight = false;
    const timer = window.setInterval(() => {
      if (reloadInFlight) return;
      reloadInFlight = true;
      void workspaces.reload().finally(() => {
        reloadInFlight = false;
      });
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [paymentInProgressKey, workspaces.reload]);
  const open = async (
    workspace: WorkspaceSummary,
    kind: "checkout" | "portal",
  ) => {
    setError("");
    setBillingBusy(workspace.id);
    try {
      const result = await api<{ url: string }>(
        `/v1/workspaces/${encodeURIComponent(workspace.id)}/billing/${kind}`,
        { method: "POST" },
      );
      window.location.assign(result.url);
    } catch (cause) {
      setBillingBusy("");
      setError(cause instanceof Error ? cause.message : "Billing failed");
    }
  };
  const respondToRecovery = async (
    workspace: WorkspaceSummary,
    review: boolean,
  ) => {
    if (!workspace.billingRecovery) return;
    setError("");
    setMessage("");
    setRecoveryBusy(workspace.id);
    try {
      await api(
        `/v1/workspaces/${encodeURIComponent(workspace.id)}/billing/recovery-review`,
        {
          method: "POST",
          body: JSON.stringify({
            recoveryId: workspace.billingRecovery.id,
            review,
          }),
        },
      );
      setMessage(
        review
          ? "Inbox review queued. Messages that are still in your Inbox will be checked, and detected spam will be moved to Gmail Spam."
          : "Inbox review skipped for this billing interruption.",
      );
      await workspaces.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Inbox review failed");
    } finally {
      setRecoveryBusy("");
    }
  };
  return (
    <Page
      title="Billing"
      subtitle="Personal Gmail stays free. Google Workspace uses one organization subscription."
    >
      {checkoutStatus === "success" && (
        <Alert tone="success">
          {workspaces.loading || paymentInProgressWorkspaceIds.length
            ? "Payment in progress. Stripe is confirming your subscription; this page checks for the latest status every few seconds."
            : "Payment confirmed. Your subscription is active."}
        </Alert>
      )}
      {checkoutStatus === "cancelled" && (
        <Alert>
          Checkout was cancelled. No subscription change was made by this return
          page.
        </Alert>
      )}
      {(error || workspaces.error) && (
        <Alert tone="error">{error || workspaces.error?.message}</Alert>
      )}
      {message && <Alert tone="success">{message}</Alert>}
      {health.data && !health.data.integrations.stripe && (
        <Alert>
          Stripe billing is dormant until the product, price, and webhook are
          configured. Personal Gmail remains free.
        </Alert>
      )}
      {recoveryOffers.map((workspace) => {
        const recovery = workspace.billingRecovery!;
        return (
          <Card key={recovery.id}>
            <Badge tone="warn">Spam filtering was paused</Badge>
            <h3>Review your inbox for the unprotected window?</h3>
            <p>
              Billing was inactive from {formatDate(recovery.paymentRequiredAt)}
              {" to "}
              {formatDate(recovery.restoredAt)}. FuckSpam can review messages
              from that window that are still in your Inbox and move detected
              spam to Gmail Spam.
            </p>
            <p>
              This only reviews your connected mailbox. Other workspace admins
              choose separately for their own inboxes.
            </p>
            {recovery.status === "failed" && (
              <Alert tone="error">
                The earlier review could not finish. You can safely retry it.
              </Alert>
            )}
            <div className="hero-actions">
              <Button
                variant="secondary"
                disabled={recoveryBusy === workspace.id}
                onClick={() => void respondToRecovery(workspace, false)}
              >
                No thanks
              </Button>
              <Button
                disabled={recoveryBusy === workspace.id}
                onClick={() => void respondToRecovery(workspace, true)}
              >
                <MailCheck size={17} /> Review my inbox
              </Button>
            </div>
          </Card>
        );
      })}
      <div className="price-cards app-pricing">
        {personals.map((personal) => (
          <Card key={personal.id}>
            <Badge tone="good">Personal</Badge>
            <h3>Personal · {short(personal.id)}</h3>
            <div className="price">$0</div>
            <p>One consumer Gmail identity and full FuckSpam protection.</p>
            <Status status="free" />
          </Card>
        ))}
        {domains.map((domain) => {
          const paymentInProgress =
            checkoutStatus === "success" &&
            domain.entitlement !== "paid" &&
            (!checkoutWorkspace || checkoutWorkspace === domain.id);
          return (
            <Card className="price-featured" key={domain.id}>
              <Badge tone="jam">{domain.hostedDomain}</Badge>
              <h3>Organization</h3>
              <div className="price">
                {domain.billingPlan === "legacy_monthly" ? "$5" : "$1"}{" "}
                <small>/ active member / month</small>
              </div>
              <p>
                {domain.activeMemberQuantity} active billable member
                {domain.activeMemberQuantity === 1 ? "" : "s"}. Trial ends{" "}
                {formatDate(domain.trialEndsAt)}.
              </p>
              <p>
                {domain.billingPlan === "legacy_monthly"
                  ? "Your existing subscription stays on its original monthly price."
                  : "Billed yearly upfront at $12 per active member after the 14-day trial."}
              </p>
              <p>
                Seat changes are prorated by Stripe within the billing period.
                If you are on annual billing, added seats are invoiced
                immediately for the rest of the year. If all active mailboxes
                are removed, the subscription is set to cancel at period end.
              </p>
              <div className="hero-actions">
                {!paymentInProgress && (
                  <Button
                    aria-busy={billingBusy === domain.id}
                    disabled={
                      health.data?.integrations.stripe === false ||
                      Boolean(billingBusy)
                    }
                    onClick={() =>
                      void open(
                        domain,
                        domain.entitlement === "paid" ? "portal" : "checkout",
                      )
                    }
                  >
                    {billingBusy === domain.id ? (
                      <>
                        <RefreshCw
                          aria-hidden="true"
                          className="billing-button-spinner"
                          size={17}
                        />
                        Redirecting…
                      </>
                    ) : (
                      <>
                        <CreditCard size={17} />{" "}
                        {domain.entitlement === "paid"
                          ? "Manage billing"
                          : "Start subscription"}
                      </>
                    )}
                  </Button>
                )}
                <Status
                  status={
                    paymentInProgress
                      ? "payment_in_progress"
                      : domain.entitlement
                  }
                />
              </div>
            </Card>
          );
        })}
      </div>
      {!domains.length && (
        <Alert>
          No domain workspace is connected. There is nothing to pay.
        </Alert>
      )}
    </Page>
  );
}

export function SecurityPage() {
  const navigate = useNavigate();
  const sessions = useLoad(endpoints.sessions, []);
  const accounts = useLoad(endpoints.accounts, []);
  const contribution = useLoad(endpoints.evaluationContribution, []);
  const [exporting, setExporting] = useState(false);
  const [contributing, setContributing] = useState(false);
  const [contributionAccountId, setContributionAccountId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!contributionAccountId && accounts.data?.accounts[0])
      setContributionAccountId(accounts.data.accounts[0].id);
  }, [accounts.data, contributionAccountId]);
  useEffect(() => {
    if (!contribution.data?.contribution) return;
    if (
      !["queued", "processing"].includes(contribution.data.contribution.status)
    )
      return;
    const timer = window.setInterval(() => void contribution.reload(), 2_000);
    return () => window.clearInterval(timer);
  }, [contribution.data?.contribution?.status, contribution.reload]);
  const submitContribution = async () => {
    if (!contributionAccountId) return;
    setContributing(true);
    setError("");
    try {
      await api("/v1/evaluation-contribution", {
        method: "POST",
        body: JSON.stringify({ accountId: contributionAccountId }),
      });
      await contribution.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Submission failed");
    } finally {
      setContributing(false);
    }
  };
  const download = async () => {
    setExporting(true);
    setError("");
    try {
      const data = await api<Record<string, unknown>>("/v1/export", {
        method: "POST",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], {
          type: "application/json",
        }),
      );
      link.download = `fuckspam-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };
  const deleteAccount = async (confirmation: string) => {
    setDeleting(true);
    setError("");
    try {
      await api("/v1/account", {
        method: "DELETE",
        body: JSON.stringify({ confirm: confirmation }),
      });
      await authClient.signOut();
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Deletion failed");
      setDeleting(false);
    }
  };
  const revokeSession = async (id: string, isCurrent: boolean) => {
    setError("");
    try {
      if (isCurrent) {
        await authClient.signOut();
        navigate("/", { replace: true });
        return;
      }
      await api(`/v1/sessions/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      await sessions.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Revocation failed");
    }
  };
  return (
    <Page
      title="Settings"
      subtitle="Manage email summaries, sessions, data, and optional evaluation contribution."
    >
      {error && <Alert tone="error">{error}</Alert>}
      {sessions.error && <Alert tone="error">{sessions.error.message}</Alert>}
      {(accounts.error || contribution.error) && (
        <Alert tone="error">
          {accounts.error?.message || contribution.error?.message}
        </Alert>
      )}
      <SpamDigestSettings />
      {!accounts.loading && Boolean(accounts.data?.accounts.length) && (
        <Card>
          <CardHeader>
            <div>
              <h3>Contribute spam for evaluation</h3>
              <p>
                Help improve FuckSpam’s system-wide detector with up to the
                latest 200 messages currently in Gmail Spam.
              </p>
            </div>
            <MailCheck />
          </CardHeader>
          <CardContent>
            {contribution.loading ? (
              <Skeleton className="h-24" />
            ) : contribution.data?.contribution?.status === "submitted" ? (
              <Alert tone="success">
                Submitted {contribution.data.contribution.sampleCount} messages
                on {formatDate(contribution.data.contribution.submittedAt)}.
              </Alert>
            ) : contribution.data?.contribution &&
              ["queued", "processing"].includes(
                contribution.data.contribution.status,
              ) ? (
              <Alert>
                Submitting the latest Spam messages securely. You can leave this
                page while FuckSpam finishes.
              </Alert>
            ) : (
              <>
                <p>
                  This is a one-time consent. FuckSpam will encrypt and retain
                  the submitted evaluation sample; it cannot be withdrawn
                  separately afterward. Submitting does not start an evaluation
                  run or change your filter.
                </p>
                <label className="field compact-field">
                  <span className="field-label">Mailbox</span>
                  <Select
                    value={contributionAccountId}
                    onChange={(event) =>
                      setContributionAccountId(event.target.value)
                    }
                  >
                    {accounts.data?.accounts.map((account) => (
                      <option value={account.id} key={account.id}>
                        {account.displayAddress}
                      </option>
                    ))}
                  </Select>
                </label>
                <Button
                  disabled={contributing || !contributionAccountId}
                  onClick={() => void submitContribution()}
                >
                  {contributing
                    ? "Submitting…"
                    : contribution.data?.contribution?.status === "failed"
                      ? "Retry submission"
                      : "Submit up to 200 Spam messages"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <div>
            <h3>Active sessions</h3>
            <p>Review devices and revoke sessions you no longer recognize.</p>
          </div>
          <LockKeyhole />
        </CardHeader>
        <CardContent>
          {sessions.loading ? (
            <Skeleton className="h-24" />
          ) : sessions.data?.sessions.length ? (
            sessions.data.sessions.map((session) => (
              <div className="list-row" key={session.id}>
                <span className="round-icon">
                  <KeyRound />
                </span>
                <div>
                  <strong>
                    {session.userAgent ?? "Unknown device"}
                    {session.isCurrent ? " · Current session" : ""}
                  </strong>
                  <span>
                    Last active {formatDate(session.updatedAt)} · expires{" "}
                    {formatDate(session.expiresAt)}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  onClick={() =>
                    void revokeSession(session.id, session.isCurrent)
                  }
                >
                  {session.isCurrent ? "Sign out" : "Revoke"}
                </Button>
              </div>
            ))
          ) : (
            <p>No active sessions were found.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div>
            <h3>Content-free export</h3>
          </div>
          <KeyRound />
        </CardHeader>
        <CardContent>
          <p>
            The export contains hashed message identifiers and scores. It never
            contains ordinary sender text, subjects, bodies, or headers.
          </p>
          <Button
            variant="secondary"
            disabled={exporting}
            onClick={() => void download()}
          >
            {exporting ? "Preparing…" : "Download export"}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div>
            <h3>Our data boundary</h3>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="check-list">
            <li>
              <Check /> No messages are ever deleted.
            </li>
            <li>
              <Check /> Ordinary classification content is processed in-memory
              and then discarded.
            </li>
            <li>
              <Check /> Stored message content is limited to explicit “Not spam”
              corrections and evaluation samples you consent to submit.
            </li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div>
            <h3>Delete FuckSpam account</h3>
            <p>
              Permanently revoke provider grants and remove your control data,
              encrypted evidence, and evaluation artifacts.
            </p>
          </div>
          <Trash2 />
        </CardHeader>
        <CardContent>
          <p>
            This cannot be undone. It does not delete or modify messages in
            Gmail.
          </p>
          <Button
            variant="secondary"
            disabled={deleting}
            onClick={() => setDeleteDialogOpen(true)}
          >
            {deleting ? "Deleting…" : "Delete account"}
          </Button>
        </CardContent>
      </Card>
      <PromptDialog
        open={deleteDialogOpen}
        title="Permanently delete FuckSpam?"
        description="This revokes Gmail access and permanently deletes your FuckSpam account, control data, encrypted evidence, and artifacts. Gmail messages are not deleted."
        label="Type DELETE to continue"
        confirmLabel="Delete account"
        requiredValue="DELETE"
        destructive
        onOpenChange={setDeleteDialogOpen}
        onConfirm={deleteAccount}
      />
    </Page>
  );
}

export function NotFoundPage() {
  return (
    <main className="center-screen">
      <Card className="auth-card">
        <CardHeader>
          <div>
            <h1>404</h1>
            <p>This page does not exist.</p>
          </div>
        </CardHeader>
        <CardContent>
          <Button render={<Link to="/dashboard" />}>Go to dashboard</Button>
        </CardContent>
      </Card>
    </main>
  );
}

function Page({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="page-body">{children}</div>
    </div>
  );
}
function PageLoading() {
  return (
    <div className="page">
      <Skeleton className="h-10 w-72" />
      <Skeleton className="mt-8 h-64" />
    </div>
  );
}
function PageError({ error }: { error: Error }) {
  return (
    <div className="page">
      <Alert tone="error">
        <CircleAlert /> {error.message}
      </Alert>
    </div>
  );
}
function Metric({
  label,
  value,
  detail,
  tone,
  action,
}: {
  label: string;
  value: string;
  detail: string;
  tone: "good" | "warn" | "bad" | "neutral";
  action?: React.ReactNode;
}) {
  return (
    <Card className={`metric-card${tone === "bad" ? " metric-card-bad" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <div>
        <span className={`dot dot-${tone}`} />
        {detail}
      </div>
      {action}
    </Card>
  );
}
function Empty({
  icon,
  title,
  text,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  text?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty">
      <span>{icon}</span>
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}
function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function Status({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const tone = [
    "active",
    "paid",
    "free",
    "passed",
    "connected",
    "repair active",
    "repair passed",
  ].includes(normalized)
    ? "good"
    : [
          "reconnect_required",
          "scope_required",
          "degraded",
          "payment_required",
          "failed",
          "needs_review",
          "repair needs_review",
        ].includes(normalized)
      ? "bad"
      : [
            "trial",
            "shadow",
            "queued",
            "running",
            "paused",
            "canary",
            "payment_in_progress",
            "repair queued",
            "repair running",
          ].includes(normalized)
        ? "warn"
        : "neutral";
  return <Badge tone={tone}>{status.replaceAll("_", " ")}</Badge>;
}
function short(id: string) {
  return id.slice(0, 8);
}
function latestStatus(runs: Array<Record<string, unknown>>) {
  if (!runs.length) return "Not run";
  const value = String(runs[0]?.status ?? "pending");
  return value[0]?.toUpperCase() + value.slice(1).replace("_", " ");
}

function evaluationSummary(value: unknown): {
  precision: number;
  recall: number;
  falsePositivesPerThousandWanted: number;
  reviewComplete: boolean;
} | null {
  try {
    const parsed = JSON.parse(String(value ?? "{}")) as {
      holdout?: Record<string, unknown>;
      reviewComplete?: unknown;
    };
    const metrics = parsed.holdout;
    if (
      !metrics ||
      typeof metrics.precision !== "number" ||
      typeof metrics.recall !== "number" ||
      typeof metrics.falsePositivesPerThousandWanted !== "number"
    )
      return null;
    return {
      precision: metrics.precision,
      recall: metrics.recall,
      falsePositivesPerThousandWanted: metrics.falsePositivesPerThousandWanted,
      reviewComplete: parsed.reviewComplete === true,
    };
  } catch {
    return null;
  }
}
